import { useMemo } from 'react'
import capitalize from 'lodash/capitalize'
import Select from 'app/frontend/base/components/Select'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import CharacterClass from 'app/frontend/types/custom/CharacterClass'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import {
  getAllClasses,
  getRolesWithMultipleSpecs,
  getSpecializationsByClass,
} from 'app/frontend/base/services/characterValues'

export const ROLE_VALUE_PREFIX = 'role:'

export const parseRoleOrSpec = (
  value: string,
): { role: CharacterRole } | { specId: number } =>
  value.startsWith(ROLE_VALUE_PREFIX)
    ? { role: value.slice(ROLE_VALUE_PREFIX.length) as CharacterRole }
    : { specId: Number(value) }

type Props = {
  characterClass?: CharacterClass
  value?: string
  setValue: (value?: string) => void
  inModal?: boolean
  className?: string
  // Also offer roles shared by multiple specs (e.g. ranged hunter), as `role:<name>` values.
  includeRoles?: boolean
}

export const classIdFor = (characterClass?: CharacterClass) => {
  const entry = Object.entries(getAllClasses()).find(
    ([, name]) => name === characterClass,
  )
  return entry ? Number(entry[0]) : undefined
}

const CharacterSpecSelect = ({
  characterClass,
  value,
  setValue,
  inModal,
  className,
  includeRoles,
}: Props) => {
  const options = useMemo(() => {
    const classId = classIdFor(characterClass)
    if (!classId) return []

    const roleOptions = includeRoles
      ? getRolesWithMultipleSpecs(classId).map((role) => ({
          value: `${ROLE_VALUE_PREFIX}${role}`,
          sortLabel: `0 ${role}`,
          label: (
            <span className='flex items-center gap-2'>
              <CharacterRoleIcon role={role as CharacterRole} />
              <span>{capitalize(role)}</span>
            </span>
          ),
        }))
      : []

    const specOptions = Object.entries(getSpecializationsByClass(classId)).map(
      ([id, spec]) => ({
        value: id,
        sortLabel: `1 ${spec.name}`,
        label: (
          <span className='flex items-center gap-2'>
            <CharacterRoleIcon role={spec.default_role as CharacterRole} />
            <BlizzardIcon iconName={spec.icon} />
            <span>{spec.name}</span>
          </span>
        ),
      }),
    )

    return [...roleOptions, ...specOptions]
  }, [characterClass, includeRoles])

  return (
    <Select
      value={value}
      setValue={setValue}
      options={options}
      disabled={!characterClass}
      isSearchable={false}
      placeholder={includeRoles ? 'Select role' : 'Select spec'}
      inModal={inModal}
      className={className}
    />
  )
}

export default CharacterSpecSelect
